import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { CheckCircle, XCircle, AlertTriangle, Shield, Clock, ChevronDown, ChevronUp } from "lucide-react";

const Navigation = () => {
  return (
    <nav className="dark-header">
      <a href="/">
        <img src="/logos/logo-v5.jpg" alt="ARCANA" className="dark-logo-nav" />
      </a>
      <div className="dark-nav">
        <a href="/" className="dark-nav-link">HOME</a>
        <a href="/resources" className="dark-nav-link">RESOURCES</a>
        <a href="/services" className="dark-nav-link">SERVICES</a>
        <a href="/faq" className="dark-nav-link">FAQ</a>
        <button className="btn-primary" onClick={() => window.location.href = '/#assessment'}>
          FREE ASSESSMENT
        </button>
      </div>
      <button className="mobile-menu-btn">
        <span style={{color: 'var(--oro-arcana)'}}>☰</span>
      </button>
    </nav>
  );
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-accordion-item ${isOpen ? 'active' : ''}`}>
      <button className="faq-accordion-button" onClick={() => setIsOpen(!isOpen)}>
        <span className="faq-accordion-question">{question}</span>
        {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
      </button>
      {isOpen && (
        <div className="faq-accordion-content">
          <p className="faq-answer">{answer}</p>
        </div>
      )}
    </div>
  );
};

const CompromisedWalletFirst30Minutes = () => {
  return (
    <div className="App">
      <Helmet>
        <title>Compromised Wallet: First 30 Minutes Checklist (UK)</title>
        <meta name="description" content="Suspect unauthorised access? Follow a calm first-30-minutes checklist to contain risk and migrate safely. Non-custodial emergency support. No promises. Free assessment." />
        <meta name="keywords" content="compromised wallet, wallet hacked, crypto scam victim, emergency wallet migration, wallet security breach, unauthorised access" />
        <link rel="canonical" href="https://arcana-crypto.com/compromised-wallet-first-30-minutes.html" />
      </Helmet>

      <Navigation />
      
      <article className="section" style={{marginTop: '128px', minHeight: '100vh'}}>
        <div className="section-container" style={{maxWidth: '900px'}}>
          
          {/* Header */}
          <header>
            <div style={{
              background: 'var(--nero-grigio)', 
              border: '2px solid var(--oro-arcana)', 
              padding: '24px',
              marginBottom: '40px'
            }}>
              <AlertTriangle size={48} color="var(--oro-arcana)" style={{display: 'block', margin: '0 auto 16px'}} />
              <h1 className="section-title" style={{fontSize: '42px', textAlign: 'center', marginBottom: '16px', color: 'var(--oro-arcana)'}}>
                Compromised Wallet: What to Do in the First 30 Minutes
              </h1>
              <p className="section-intro" style={{fontSize: '18px', marginBottom: '0', textAlign: 'center'}}>
                If you suspect your wallet is <strong>compromised</strong>, speed matters—but so does discipline. 
                The goal is <strong>containment</strong>, <strong>safe migration</strong>, and <strong>evidence preservation</strong>.
              </p>
            </div>
          </header>

          {/* Key Takeaways */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              Key Takeaways
            </h2>
            <ul className="column-list">
              <li>Do not panic-transfer using unknown tools.</li>
              <li>Do not share seeds or private keys.</li>
              <li>Isolate devices and preserve evidence.</li>
              <li>Migration can be non-custodial, but must be executed carefully.</li>
              <li>Outcomes vary; no promises.</li>
            </ul>
          </section>

          {/* What to Do */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <CheckCircle size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              What to Do (First 30 Minutes)
            </h2>
            <div className="about-card">
              <ol className="column-list" style={{listStyleType: 'decimal', paddingLeft: '20px'}}>
                <li><strong>Disconnect and isolate</strong> potentially infected devices.</li>
                <li><strong>Assess immediate risk</strong> (pending transactions, attacker activity on blockchain).</li>
                <li><strong>Prepare a clean environment</strong> (verified device, updated antivirus).</li>
                <li><strong>Plan migration to new wallet</strong> (fresh seed, secure storage).</li>
                <li><strong>Preserve evidence</strong> (transaction IDs, screenshots, timestamps, attacker addresses).</li>
              </ol>
            </div>
          </section>

          {/* What to Avoid */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <XCircle size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              What to Avoid
            </h2>
            <div className="about-card">
              <ul className="column-list">
                <li>Do not enter your seed on a <strong>compromised device</strong>.</li>
                <li>Do not install random "security scanners".</li>
                <li>Do not engage with the attacker.</li>
                <li>Avoid public posts with identifying details.</li>
              </ul>
            </div>
          </section>

          {/* Technical Explanation */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <Clock size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              Technical Explanation (Simplified)
            </h2>
            <div className="legal-content">
              <p>
                <strong>Compromise</strong> can come from malware, phishing approvals (EVM), seed exposure, 
                or clipboard hijacking. <strong>Containment</strong> focuses on cutting off signing capability 
                and moving assets to a clean wallet.
              </p>
              <p style={{marginTop: '16px'}}>
                On <strong>Ethereum/EVM chains</strong>, revoking token approvals may be part of the response 
                (use tools like Revoke.cash on a clean device). For <strong>Bitcoin</strong>, creating a new 
                wallet with a fresh seed and transferring remaining funds is the primary containment method.
              </p>
              <p style={{marginTop: '16px'}}>
                Time is critical: attackers often monitor wallets and may attempt to move funds once they 
                detect activity. Our <strong>emergency wallet migration service</strong> (£500, 30-minute response) 
                provides guided containment for active threats.
              </p>
            </div>
          </section>

          {/* Non-Custodial & Privacy */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <Shield size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              Non-Custodial & Privacy
            </h2>
            <div className="about-card">
              <p className="legal-content" style={{marginBottom: '16px'}}>
                Non-custodial emergency support with clear scope, encrypted comms, data minimisation 
                and deletion aligned with GDPR principles.
              </p>
              <ul className="column-list">
                <li>We guide migration process - you execute transactions</li>
                <li>No custody or control of funds</li>
                <li>Encrypted communication channels (WhatsApp, Telegram, Signal)</li>
                <li>Evidence preservation guidance for potential legal action</li>
              </ul>
            </div>
          </section>

          {/* When Recovery is Feasible */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              When Recovery is Feasible
            </h2>
            <div className="about-card">
              <p className="legal-content">
                <strong>More feasible</strong> when you act quickly and still control signing capability. 
                Early detection and rapid response significantly improve containment outcomes.
              </p>
              <p className="legal-content" style={{marginTop: '16px'}}>
                <strong>Less feasible</strong> when funds are already moved or the seed is fully exposed 
                to attackers with active monitoring. Assessment determines realistic options.
              </p>
            </div>
          </section>

          {/* What We Need */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              What We Need for Assessment
            </h2>
            <div className="pricing-notes">
              <ul className="column-list">
                <li>Network and wallet type</li>
                <li>Affected addresses (public addresses only)</li>
                <li>What you observed (unauthorized transactions, phishing approval, device infection)</li>
                <li>Urgency level (active threat vs suspected exposure)</li>
                <li>Whether you still control the wallet (can you sign?)</li>
              </ul>
            </div>
          </section>

          {/* FAQ */}
          <section style={{marginBottom: '80px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '32px'}}>
              Frequently Asked Questions
            </h2>
            <div className="faq-accordion">
              <FAQItem 
                question="Can you guarantee funds will be saved?"
                answer="No. Compromise outcomes are highly variable. We provide guidance and containment strategies, but cannot guarantee asset recovery when attackers have access."
              />
              <FAQItem 
                question="Should I contact the police?"
                answer="For significant losses, yes. We can provide forensic documentation and evidence to support police reports or legal action."
              />
              <FAQItem 
                question="Do I need to share my seed?"
                answer="Not via chat. Emergency migration guidance can often be provided without seed exposure. Secure handling discussed case-by-case."
              />
              <FAQItem 
                question="Is revoking approvals enough?"
                answer="Sometimes for EVM token approvals, but wallet migration is often safer if seed exposure is suspected."
              />
              <FAQItem 
                question="How fast can you respond?"
                answer="Case-dependent; active threats are prioritised. Emergency service: 30-minute response target."
              />
              <FAQItem 
                question="Can you trace stolen funds?"
                answer="Forensics/reporting possible; fund recovery not guaranteed. We can provide blockchain analysis for authorities."
              />
              <FAQItem 
                question="What if I was scammed (not hacked)?"
                answer="We can advise on containment and provide forensic documentation. Fund recovery from scammers is typically not feasible."
              />
            </div>
          </section>

          {/* Emergency CTA */}
          <section className="cta-section" style={{
            marginTop: '80px', 
            padding: '60px 48px',
            background: 'linear-gradient(135deg, var(--nero-profondo), var(--nero-morbido))',
            border: '2px solid var(--oro-arcana)'
          }}>
            <AlertTriangle size={64} color="var(--oro-arcana)" style={{margin: '0 auto 24px'}} />
            <h2 className="cta-title" style={{fontSize: '36px', color: 'var(--oro-arcana)'}}>
              Active Threat? Get Help Now
            </h2>
            <p className="cta-subtitle" style={{marginBottom: '40px'}}>
              Start with the fastest channel (WhatsApp/Telegram) and choose assessment priority
            </p>
            
            <div style={{display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '24px'}}>
              <a 
                href="https://wa.me/447835822143?text=EMERGENCY:%20My%20wallet%20is%20compromised"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                style={{fontSize: '16px', padding: '16px 32px'}}
              >
                💬 WHATSAPP EMERGENCY
              </a>
              <a 
                href="https://t.me/Arcana_Crypto_Lab"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                style={{fontSize: '16px', padding: '16px 32px'}}
              >
                🔐 TELEGRAM EMERGENCY
              </a>
            </div>
            
            <button className="btn-secondary" style={{fontSize: '16px', padding: '14px 32px'}} onClick={() => window.location.href = '/#assessment'}>
              EMAIL ASSESSMENT (Non-Urgent)
            </button>
            
            <p className="hero-small-text" style={{marginTop: '24px'}}>
              Emergency response: 30 minutes target • Non-custodial guidance • Evidence preservation
            </p>
            
            <div style={{marginTop: '32px'}}>
              <p style={{color: 'var(--grigio-chiaro)', marginBottom: '12px'}}>
                <strong>Related Services:</strong>
              </p>
              <a href="/services" className="card-link" style={{fontSize: '16px', marginRight: '20px', color: 'var(--oro-arcana)'}}>
                Emergency Services →
              </a>
              <a href="/crypto-recovery-service-uk" className="card-link" style={{fontSize: '16px', color: 'var(--oro-arcana)'}}>
                UK Emergency Support →
              </a>
            </div>
          </section>

          {/* Breadcrumb Back */}
          <div style={{marginTop: '60px', textAlign: 'center'}}>
            <a href="/resources" className="card-link" style={{fontSize: '16px'}}>
              ← Back to All Resources
            </a>
          </div>

        </div>
      </article>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-bottom">
            <p className="footer-copyright">
              © 2025-2026 ARCANA CRYPTO LTD - All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CompromisedWalletFirst30Minutes;
